import { Loader } from '@creditsights/styleguide-web';
import isEmpty from 'lodash/isEmpty';
import { Layout, PlotData, PlotlyHTMLElement } from 'plotly.js-basic-dist-min';
import React, { ReactElement, useEffect, useRef, useState } from 'react';
import { CqsMetric } from '../../../../models/RiskProducts';
import { formatCommonDate } from '../../../../utils/dateUtils';
import ChartContainer from '../../../ChartContainer';
import ChartPlotly from '../ChartPlotly';
import MetricTags from '../MetricTags/MetricTags';
import RiskProductsTooltip from '../RiskProductsTooltip/RiskProductsTooltip';
import { getMetricTag, getTooltipRiskMetricData } from '../RiskProductsWidget.logic';
import { composePlotParams } from './CqsChart.logic';
import { plotDefaultConfig } from '../RiskProductsWidget.presentational.constants';
import { infoTooltipText } from './CqsChart.presentational.constants';
import { TooltipData } from './CqsChart.types';
import CqsChartTooltipContent from './Tooltip/CqsChartTooltipContent';

interface CqsChartProps {
  cqsMetrics: CqsMetric[];
}

const CqsChart = ({ cqsMetrics }: CqsChartProps): ReactElement => {
  const [plotData, setPlotData] = useState<Partial<PlotData>[] | null>(null);
  const [plotLayout, setPlotLayout] = useState<Partial<Layout>>(null);
  const plotElement = useRef<PlotlyHTMLElement>(null);
  const [tooltipData, setTooltipData] = useState<TooltipData | null>(null);

  const mostRecentCQS = getMetricTag(cqsMetrics[0].cqs);
  const mostRecentACR = getMetricTag(cqsMetrics[0].acr);
  const mostRecentActionDay = getMetricTag(formatCommonDate(cqsMetrics[0].calculationDate));

  useEffect(() => {
    const [updatedPlotData, updatedPlotLayout] = composePlotParams(cqsMetrics, plotData, plotLayout);

    setPlotData(updatedPlotData);
    setPlotLayout(updatedPlotLayout);
  }, [cqsMetrics]);

  useEffect(() => {
    const plot = plotElement?.current;

    if (plot) {
      /* eslint-disable no-underscore-dangle */
      plot.on('plotly_hover', (data) => {
        const points = data.points.find((point) => point.data.mode === 'lines');
        const pointNumber = points?.pointNumber;
        const positionLeft = data.points[0].xaxis.l2p(data.points[0].x) + data.points[0].xaxis._offset;
        const firstLineTop = data.points[0].yaxis.l2p(data.points[0].y) + data.points[0].yaxis._offset;
        const secondLineTop = data.points[1].yaxis.l2p(data.points[1].y) + data.points[1].yaxis._offset;
        const positionTop = Math.min(firstLineTop, secondLineTop);

        const tooltipData = {
          positionLeft,
          positionTop,
          cqsMetric: getTooltipRiskMetricData(cqsMetrics, pointNumber),
        };
        setTooltipData(tooltipData);
      });
      /* eslint-enable no-underscore-dangle */

      plot.on('plotly_unhover', () => {
        setTooltipData(null);
      });
    }
  }, [plotData]);

  return (
    <div data-testid="cqs-chart">
      <ChartContainer>
        {!isEmpty(plotData) ? (
          <>
            <ChartPlotly ref={plotElement} data={plotData} layout={plotLayout} config={plotDefaultConfig} />
            {!!tooltipData && (
              <RiskProductsTooltip
                id={plotElement?.current.id}
                positionTop={tooltipData.positionTop}
                positionLeft={tooltipData.positionLeft}
              >
                <CqsChartTooltipContent cqsMetric={tooltipData.cqsMetric} />
              </RiskProductsTooltip>
            )}
          </>
        ) : (
          <Loader position="center" text={null} />
        )}
      </ChartContainer>
      <MetricTags
        firstTag={`CQS: ${mostRecentCQS}`}
        secondTag={`ACR: ${mostRecentACR}`}
        date={mostRecentActionDay}
        infoTooltipText={infoTooltipText}
      />
    </div>
  );
};

export default CqsChart;
